import { useState, type ReactNode } from 'react';
import { formatDuration } from '@vendorops/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TimePicker from '@/components/TimePicker';
import HoursMinutesField, {
  DURATION_TOO_LONG_HINT,
  durationFields,
  durationOutOfRange,
  minutesFromFields,
  type HoursMinutes,
} from '@/components/HoursMinutesField';

/**
 * What every part of the timeline agrees a part of the day *is*.
 *
 * The row this file is named for is gone: the day is an axis now, and a block
 * is edited where it sits rather than by swapping a row for a form. What
 * survives is the vocabulary that was never about being a row — the server's
 * resolved item, the draft a planner fills in, the three answers a duration
 * can give, the labelled field wrapper, and the one form left standing.
 *
 * Everything positional — the clock time, which calendar day it lands on, how
 * long it actually runs, whether it spills past the end of the night — arrives
 * resolved from the server. Nothing here recomputes any of it: two
 * implementations of the wrap rule is one more than the number that can be
 * right, and the one that would be wrong is this one, which cannot see the
 * item's neighbours or the wedding's hours.
 */

/** The server's resolved item. Stored fields, then what it derived from them. */
export interface TimelineItemView {
  id: string;
  label: string;
  offsetMinutes: number;
  durationMinutes: number | null;
  note: string | null;
  /** `'21:30'`, wall-clock at the venue. */
  clockTime: string;
  /** -1, 0 or 1 calendar days from the wedding's own date. */
  dayOffset: number;
  /** **Null and 0 are different answers** — see the duration line below. */
  resolvedDurationMinutes: number | null;
  endsAfterWedding: boolean;
  /** Where it finishes, or null when it is open-ended. Derived on the server
   *  alongside `clockTime`, so the two cannot disagree about midnight. */
  endClockTime: string | null;
  endDayOffset: number | null;
}

/** What a planner fills in, in the terms they think in. */
export interface TimelineItemDraft {
  /** `'HH:MM'`. The server turns this into an offset; no signed integer is ever
   *  shown to, or accepted from, a planner. */
  clockTime: string;
  label: string;
  /** Null is open-ended: runs to the next item, or to the end of the night. */
  durationMinutes: number | null;
  note: string | null;
}

/**
 * What a click on the axis creates.
 *
 * An offset, where `TimelineItemDraft` carries a clock time, and the
 * difference is not cosmetic. A planner typing "4:15" into the add form has
 * not said which day they mean, and `offsetForClockTime`'s twelve-hour window
 * is the documented rule for guessing. A click has named a pixel, and a pixel
 * is already an offset — handing it to the guess loses the only unambiguous
 * thing about it. Against a 5:30 PM wedding the guess turns midnight at the
 * top of the axis into midnight at the foot of it.
 *
 * `requestedOffset` on the server takes either shape and prefers the offset,
 * so both gestures stay distinguishable the whole way down.
 */
export interface TimelineItemPlacement {
  offsetMinutes: number;
  label: string;
  /** Null is open-ended: runs to the next item, or to the end of the night. */
  durationMinutes: number | null;
  note: string | null;
}

/** What a zero-length item is, in the same voice as a length. */
const MOMENT_TEXT = 'A moment';

/**
 * How long this item runs, in words.
 *
 * Three answers, not two. `null` is open-ended: no stored length and nothing
 * after it to run up against. `0` is a point in time — a planner scheduling a
 * send-off for the exact moment the night ends means it — and it says so rather
 * than showing nothing, because a blank where every other row carries a length
 * reads as a length that went missing. Anything else is a duration.
 *
 * Reading the first two with one truthiness check collapses them and prints
 * "open-ended" over the ordinary case. Neither reading is a warning: both are
 * things a planner deliberately scheduled.
 */
export function durationText(minutes: number | null): string {
  if (minutes === null) return 'Open-ended';
  if (minutes === 0) return MOMENT_TEXT;
  return formatDuration(minutes);
}

/**
 * The form for adding an item from the panel's own header.
 *
 * Not for editing any more: a wedding that already has a day is edited on the
 * axis, a field at a time, and a click on empty axis space starts a new item
 * there with only its label to fill in. This is what is left for the one case
 * the axis cannot offer — a timeline with nothing on it yet, which has no
 * empty space to click because it has no axis.
 *
 * The time field is the same `TimePicker` the wedding's hours use, so `9:45`,
 * `945` and `9:45 PM` all land here too, and the length is the same
 * `HoursMinutesField` the template editor places an item with — a planner says
 * "an hour and a half" in one grammar wherever they are.
 *
 * No `after` anchor on that picker, deliberately. Half the default ladder is
 * prep work before the wedding starts, so anchoring the AM/PM reading to the
 * start time would read a 2:00 PM flat-lay as 2:00 AM. The unanchored reading
 * treats a bare hour as PM, which covers both the afternoon and the evening;
 * the small hours are typed as `1am`.
 */
export function TimelineItemForm({
  idPrefix,
  initial,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  initial: TimelineItemDraft;
  submitLabel: string;
  pending?: boolean;
  onSubmit: (draft: TimelineItemDraft) => void;
  onCancel: () => void;
}) {
  const [clockTime, setClockTime] = useState(initial.clockTime);
  const [label, setLabel] = useState(initial.label);
  // Two fields rather than a minute count, and held as text: an emptied pair is
  // a legitimate answer — it means open-ended — and a typed `0` is a different
  // legitimate one. `durationFields` is what keeps a stored zero visible as one.
  const [duration, setDuration] = useState<HoursMinutes>(durationFields(initial.durationMinutes));
  const [note, setNote] = useState(initial.note ?? '');

  const parsedDuration = minutesFromFields(duration);
  const durationInvalid = durationOutOfRange(parsedDuration);

  /**
   * What the pair means, read back as it is typed.
   *
   * In the same words the rows use, so the form and the row it becomes cannot
   * disagree — and it is the only thing on screen that tells an emptied pair from
   * a typed zero, the two answers that look alike and mean opposite things.
   */
  const durationHint = durationInvalid
    ? DURATION_TOO_LONG_HINT
    : durationText(parsedDuration);

  const ready = clockTime !== '' && label.trim() !== '' && !durationInvalid;

  return (
    <form
      className="space-y-3 rounded-2xl bg-[var(--color-surface-container-low)] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        onSubmit({
          clockTime,
          label: label.trim(),
          durationMinutes: parsedDuration,
          note: note.trim() === '' ? null : note.trim(),
        });
      }}
      onKeyDown={(e) => {
        // `defaultPrevented` is how the time field says it handled the key
        // itself: Escape with its menu open closes the menu, and closing a menu
        // is not abandoning the edit.
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <div className="flex flex-wrap items-start gap-3">
        <Field className="w-32" id={`${idPrefix}-time`} label="Time">
          {/* TimePicker names itself from `label`, so the visible label and the
              accessible one are the same word. */}
          <TimePicker
            id={`${idPrefix}-time`}
            label="Time"
            placeholder="7:30 PM"
            value={clockTime}
            onChange={setClockTime}
          />
        </Field>

        <Field className="min-w-48 flex-1" id={`${idPrefix}-label`} label="What happens">
          <Input
            id={`${idPrefix}-label`}
            value={label}
            maxLength={120}
            placeholder="Ceremony"
            onChange={(e) => setLabel(e.target.value)}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <HoursMinutesField
          idPrefix={`${idPrefix}-duration`}
          legend="How long it runs"
          hoursLabel="Length in hours"
          minutesLabel="Length in minutes"
          value={duration}
          onChange={setDuration}
          invalid={durationInvalid}
          hint={durationHint}
        />

        <Field className="min-w-48 flex-1" id={`${idPrefix}-note`} label="Note">
          <Input
            id={`${idPrefix}-note`}
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!ready || pending}>
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function Field({
  id,
  label,
  hint,
  className,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <label htmlFor={id} className="label-caps mb-1 block text-[0.625rem] text-foreground/70">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-[0.6875rem] text-foreground/70">{hint}</p>}
    </div>
  );
}

/**
 * Which day this lands on, when it is not the wedding's own.
 *
 * A bare `1:00 AM` on an axis that opens at midnight on the wedding's own date
 * reads as 1:00 that morning — 1050 minutes above the ceremony rather than 450
 * below it. The axis grows a whole second day to hold a block like this; the
 * label is what names which day the reader is looking at.
 *
 * Here rather than on the block because three surfaces name days now — the
 * block, the axis's own day dividers, and a coverage window whose end falls
 * after midnight — and three copies of these words would drift. Null on the wedding's own day is this *caller's*
 * policy, not a hole in the vocabulary — a block there needs no marker.
 * `relativeDayLabel` in TimelineAxis layers its own answer over the null.
 */
export function dayText(dayOffset: number): string | null {
  if (dayOffset === 0) return null;
  if (dayOffset === 1) return 'Next morning';
  if (dayOffset === -1) return 'The day before';
  return dayOffset > 0 ? `${dayOffset} days later` : `${-dayOffset} days before`;
}
